<script>
import { mapGetters } from 'vuex';
import { FormUtils } from '../../utils/FormUtils';
import DeliveryDistributionTable from '../Deliveries/DeliveryDistributionTable.vue';

export default {
  components: {
    DeliveryDistributionTable,
  },
  name: 'pos-step-delivery',
  props: {
    currentStep: Number,
    pickupStoreId: Number,
  },
  data: () => ({
    clientPickupAuthorized: false,
    authorizedClientName: null,
    authorizedClientContact: null,
    authorizedClientEmail: null,
    deliveryValidationAttempted: false,
    itemPickupStoreByItemKey: {},
    deliveryTableHeaders: [
      {
        text: 'Producto',
        value: 'name',
        align: 'start',
        sortable: false,
      },
      {
        text: 'Total',
        value: 'quantity',
        align: 'start',
        sortable: false,
      },
      {
        text: 'Estatus',
        value: 'status',
        align: 'center',
        sortable: false,
      },
    ],
    errorDetail: '',
  }),
  computed: {
    ...mapGetters({
      stores: 'stores',
      selectedClient: 'selectedClient',
      hasItemsWithNoInventory: 'hasItemsWithNoInventory',
      itemsWithNoInventory: 'itemsWithNoInventory',
    }),
    itemsToDistribute() {
      return this.itemsWithNoInventory.length || 0;
    },
    hasDistributionData() {
      return this.$refs.deliveryDistributionTable?.hasDistributionData() || false;
    },
  },
  watch: {
    async currentStep(newStep) {
      if (newStep === 2) {
        if (!this.hasDistributionData) {
          this.initComponent();
        }
      } else {
        await this.$refs.deliveryDistributionTable.reset();
        this.authorizedClientName = '';
        this.authorizedClientContact = '';
        this.authorizedClientEmail = '';
        this.errorDetail = '';
        this.deliveryValidationAttempted = false;
        if (this.$refs.deliveryForm) {
          this.$refs.deliveryForm.resetValidation();
        }
      }
    },
  },
  methods: {
    sendAllToCurrentStore() {
      this.$refs.deliveryDistributionTable.sendAllToCurrentStore();
    },
    trimMax(value, max = 100) {
      return FormUtils.trimMax(value, max);
    },
    onlyDigits(value) {
      return String(value || '').replace(/\D/g, '');
    },
    onClientDeliveryCheckboxChange(checked) {
      if (checked) {
        this.authorizedClientName = this.selectedClient.name || '';
        if (this.selectedClient.contactInfo) {
          this.authorizedClientContact = this.selectedClient.contactInfo.phoneNumber || '';
          this.authorizedClientEmail = this.selectedClient.contactInfo.email || '';
        }
      } else {
        this.authorizedClientName = '';
        this.authorizedClientContact = '';
        this.authorizedClientEmail = '';
      }
      this.deliveryValidationAttempted = false;
    },
    validateAuthorizedClientName(value) {
      const authorizedName = this.trimMax(value, 100);
      if (!authorizedName) return 'Nombre del titular requerido';
      return true;
    },
    validateAuthorizedClientContact(value) {
      const contactDigits = this.onlyDigits(value);
      if (!contactDigits) return 'Telefono de contacto requerido';
      if (contactDigits.length < 10 || contactDigits.length > 15) {
        return 'Telefono de contacto invalido (10 a 15 digitos)';
      }
      return true;
    },
    validateAuthorizedClientEmail(value) {
      const email = this.trimMax(value, 100);
      if (!email) return 'Correo electronico requerido';
      if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
        return 'Correo electronico invalido';
      }
      return true;
    },
    allowOnlyNumericInput(e) {
      return FormUtils.allowOnlyNumericInput(e);
    },
    getAuthorizedClientData() {
      return {
        name: this.authorizedClientName,
        contact: this.authorizedClientContact,
        email: this.authorizedClientEmail,
      };
    },
    validateForm() {
      if (!this.hasItemsWithNoInventory) return true;
      if (!this.pickupStoreId) return false;

      if (!this.hasDistributionData) return false;
      const hasItemsNotAssigned = this.$refs.deliveryDistributionTable.validateForm();
      if (hasItemsNotAssigned) {
        this.errorDetail = 'Asigna todos los productos';
        return false;
      }

      this.$refs.deliveryForm.validate();

      const nameValidation = this.validateAuthorizedClientName(this.authorizedClientName);
      if (nameValidation !== true) return nameValidation;

      const contactValidation = this.validateAuthorizedClientContact(this.authorizedClientContact);
      if (contactValidation !== true) return contactValidation;

      const emailValidation = this.validateAuthorizedClientEmail(this.authorizedClientEmail);
      if (emailValidation !== true) return emailValidation;

      return true;
    },
    saveData(products) {
      const assignedProducts = this.$refs.deliveryDistributionTable.applyPickupStoreToProducts(products);
      this.$store.dispatch('setDeliverySaleData', {
        client: this.getAuthorizedClientData(),
        products: assignedProducts,
      });
    },
    initComponent() {
      this.errorDetail = '';
      this.deliveryValidationAttempted = false;
      this.authorizedClientName = '';
      this.authorizedClientContact = '';
      this.authorizedClientEmail = '';
      if (this.$refs.deliveryForm) {
        this.$refs.deliveryForm.resetValidation();
      }
      this.$refs.deliveryDistributionTable.initializePickupDistribution();
    },
  },
  mounted() {
    this.initComponent();
  },
};
</script>

<template>
<div>
  <div class="section-box">
    <div class="d-flex justify-space-between align-center mb-2">
      <div>
        <v-icon class="mr-1" color="primary">mdi-account-plus</v-icon>
        <span class="text-body-2 font-weight-bold">Responsable del Retiro</span>
      </div>
      <v-checkbox
        v-model="clientPickupAuthorized"
        @change="onClientDeliveryCheckboxChange"
        label="El cliente titular de la cuenta recoge"
        hide-details
        class="mt-0"
        :disabled="selectedClient === null"
      ></v-checkbox>
    </div>
    <v-form ref="deliveryForm" @submit.prevent>
      <div class="d-flex justify-space-between">
        <v-text-field
          class="px-1"
          v-model="authorizedClientName"
          dense
          label="Nombre del quien recoge"
          outlined
          maxlength="100"
          counter="100"
          :rules="[validateAuthorizedClientName]"
          :error-messages="deliveryValidationAttempted && validateAuthorizedClientName(authorizedClientName) !== true ? [validateAuthorizedClientName(authorizedClientName)] : []"
          hint="Obligatorio."
          persistent-hint
          prepend-inner-icon="mdi-account"
        ></v-text-field>
        <v-text-field
        class="px-1"
          v-model="authorizedClientContact"
          dense
          label="Telefono de contacto *"
          outlined
          type="text"
          inputmode="numeric"
          maxlength="10"
          counter="10"
          @input="authorizedClientContact = onlyDigits($event || '').slice(0, 10)"
          @keydown="allowOnlyNumericInput"
          :rules="[validateAuthorizedClientContact]"
          :error-messages="deliveryValidationAttempted && validateAuthorizedClientContact(authorizedClientContact) !== true ? [validateAuthorizedClientContact(authorizedClientContact)] : []"
          hint="Obligatorio. 10 digitos."
          persistent-hint
          prepend-inner-icon="mdi-phone"
        ></v-text-field>
        <v-text-field
          class="px-1"
          v-model="authorizedClientEmail"
          dense
          label="Correo electronico *"
          outlined
          type="email"
          maxlength="100"
          counter="100"
          :rules="[validateAuthorizedClientEmail]"
          :error-messages="deliveryValidationAttempted && validateAuthorizedClientEmail(authorizedClientEmail) !== true ? [validateAuthorizedClientEmail(authorizedClientEmail)] : []"
          hint="Obligatorio. Ejemplo: nombre@dominio.com"
          persistent-hint
          prepend-inner-icon="mdi-email"
        ></v-text-field>
      </div>
    </v-form>
  </div>
  <div>
    <div class="d-flex justify-space-between align-center mb-2">
      <div>
        <v-icon class="mr-1" color="primary">mdi-account-plus</v-icon>
        <span class="text-body-2 font-weight-bold">Distribución por Sucursal</span>
      </div>
      <v-chip>
        <span class="text-caption font-weight-bold">{{ itemsToDistribute }} {{ itemsToDistribute === 1 ? 'artículo' : 'artículos' }}</span>
      </v-chip>
      <v-btn
        text
        @click="sendAllToCurrentStore"
      >Restablacer todo a sucursal actual</v-btn>
    </div>
    <DeliveryDistributionTable
      ref="deliveryDistributionTable"
      :pickupStoreId="pickupStoreId"
    />
  </div>
  <span
    v-if="errorDetail"
    class="text-caption text-error"
  >*{{ errorDetail }}</span>
</div>
</template>

<style lang="less">
.section-box {
  border: 1px solid #e0e0e0;
  border-radius: 4px;
  padding: 8px 8px;
  margin-bottom: 16px;
}
.text-error {
  color: #d32f2f;
}
</style>
